import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { getUserType } from "../../services/auth";
import api from '../../services/api';
import { readAllArchives } from '../../services/history-archive';

import Header from '../../components/header';
import TitlePage from '../../components/title-page';
import Search from '../../components/search';

// Submission History: solo lectura. Agrupa por publisher todos los envios de
// proyectos que le han llegado, con los contactos que los recibieron y si el
// grupo que los mando sigue existiendo. Desde aqui no se envia nada: es la vista
// de consulta que complementa al historial por proyecto de Follow-Up.
export default class SubmissionHistory extends Component {
	state = {
		not_admin: false,
		loading: true,
		loading_step: "",
		error: "",
		groups: [],
		projects: {},
		companies: new Map(),
		file_titles: {},
		search: "",
		open_publisher: null,
		details_loading: false
	}

	// Los archivos de un envio y los titulos de un proyecto se piden al desplegar
	// y se recuerdan aqui: fuera del state porque solo sirven para no repetir
	// peticiones, no para decidir que pintar.
	loaded_list_files = {}

	loaded_project_files = {}

	// Mismo tamano de lote que Follow-Up: con 250+ publishers, lanzarlas todas a
	// la vez es lo que disparaba timeouts.
	BATCH_SIZE = 12

	componentDidMount() {
		document.querySelector('body').classList.remove("no-scroll");

		if (getUserType() !== "admin") {
			return this.setState({not_admin: true});
		}

		this.loadHistory();
	}

	// ----------------------------------------------------
	// CARGA ----------------------------------------------
	// ----------------------------------------------------

	loadHistory = async () => {
		this.setState({loading: true, error: "", loading_step: "Loading publishers..."});

		try {
			const publishers = await this.loadPublishers();

			this.setState({loading_step: "Loading sent groups..."});
			const lists = await this.loadSubmissionLists();

			const sends = this.buildSends(lists);

			// Las companias primero: los proyectos se piden por compania.
			this.setState({loading_step: "Loading companies..."});
			const companies = await this.loadCompanies();
			const projects = await this.loadProjects(companies);

			// Los grupos se arman una sola vez y viven en el state. Rehacerlos en cada
			// render costaria recorrer todos los envios por cada tecla del buscador y,
			// peor, tiraria los archivos que loadGroupDetails deja en cada envio: las
			// filas son copias, y una copia nueva vuelve a nacer sin ellos.
			this.setState({
				groups: this.buildGroups(publishers, sends),
				projects,
				companies,
				loading: false,
				loading_step: ""
			});
		} catch (error) {
			console.log(error);
			this.setState({
				loading: false,
				loading_step: "",
				error: "The submission history could not be loaded. Please try again."
			});
		}
	}

	// Un solo include trae los publishers con sus contactos. Si el servidor no lo
	// acepta (o lo ignora y devuelve los publishers pelados) se cae al camino
	// largo, que es el que ya usa Follow-Up: una peticion por publisher, en lotes.
	loadPublishers = async () => {
		try {
			const filter = encodeURIComponent(JSON.stringify({include: ["employees"]}));
			const response = await api.get(`publishers?filter=${filter}`);
			const publishers = response.data || [];

			if (publishers.some(publisher => Array.isArray(publisher.employees))) {
				return publishers.map(publisher => ({
					...publisher,
					collaborators: publisher.employees || []
				}));
			}

			return this.loadPublisherEmployees(publishers);
		} catch (error) {
			console.log(error);

			const response = await api.get(`publishers`);

			return this.loadPublisherEmployees(response.data || []);
		}
	}

	loadPublisherEmployees = async (publishers) => {
		for (let i = 0; i < publishers.length; i += this.BATCH_SIZE) {
			const batch = publishers.slice(i, i + this.BATCH_SIZE);

			this.setState({
				loading_step: `Loading contacts ${Math.min(i + this.BATCH_SIZE, publishers.length)} of ${publishers.length}...`
			});

			await Promise.all(batch.map(async (publisher) => {
				try {
					const response = await api.get(`publishers/${publisher.id}/employees`);
					publisher.collaborators = response.data || [];
				} catch (error) {
					// Un fallo aislado no puede dejar sin tabla a los demas publishers.
					console.log(error);
					publisher.collaborators = [];
				}
			}));
		}

		return publishers;
	}

	// Todos los grupos ya enviados, de todos los proyectos, en una peticion. Es lo
	// que hace viable esta pagina: pedirlos proyecto por proyecto serian cientos
	// de llamadas.
	loadSubmissionLists = async () => {
		const filter = encodeURIComponent(JSON.stringify({
			where: {sent_at: {neq: null}},
			include: ["contacts", "files"]
		}));

		try {
			const response = await api.get(`project_submission_lists?filter=${filter}`);
			const lists = (response.data || []).filter(list => !!list.sent_at);

			if (lists.some(list => Array.isArray(list.contacts))) {
				return lists;
			}

			// El include no vino: los contactos se piden uno a uno. Los archivos no,
			// que esos se cargan al desplegar cada publisher.
			return this.loadListContacts(lists);
		} catch (error) {
			console.log(error);

			// Sin where ni include: la coleccion pelada y el filtro en el cliente.
			const response = await api.get(`project_submission_lists`);
			const lists = (response.data || []).filter(list => !!list.sent_at);

			return this.loadListContacts(lists);
		}
	}

	loadListContacts = async (lists) => {
		for (let i = 0; i < lists.length; i += this.BATCH_SIZE) {
			const batch = lists.slice(i, i + this.BATCH_SIZE);

			this.setState({
				loading_step: `Loading recipients ${Math.min(i + this.BATCH_SIZE, lists.length)} of ${lists.length}...`
			});

			await Promise.all(batch.map(async (list) => {
				try {
					const response = await api.get(`/project_submission_lists/${list.id}/contacts`);
					list.contacts = response.data || [];
				} catch (error) {
					console.log(error);
					list.contacts = [];
				}
			}));
		}

		return lists;
	}

	// Las companias salen de app_users, igual que en List Projects: es la llamada
	// que la app ya usa para que un admin las vea todas. Sin filtrar por activas,
	// que un proveedor dado de baja no borra los envios que se le hicieron.
	loadCompanies = async () => {
		const companies = new Map();

		try {
			const response = await api.get(`app_users?filter[include]=companies`);

			(response.data || []).forEach(user => {
				(user.companies || []).forEach(company => {
					if (company && company.id != null) {
						companies.set(company.id, company.name || "");
					}
				});
			});
		} catch (error) {
			console.log(error);
		}

		return companies;
	}

	// Los titulos salen de companies/{id}/projects, que es la ruta que la app usa
	// en todas partes para listar proyectos.
	//
	// La coleccion /projects existe, pero se filtra con headers propios
	// (is_template, sent_to_publishers) en vez de con un filter de LoopBack: el
	// where con inq no resolvia nada y la columna acababa mostrando "Project #270"
	// en vez del nombre.
	loadProjects = async (companies) => {
		const ids = Array.from(companies.keys());
		const projects = {};

		for (let i = 0; i < ids.length; i += this.BATCH_SIZE) {
			const batch = ids.slice(i, i + this.BATCH_SIZE);

			this.setState({
				loading_step: `Loading projects ${Math.min(i + this.BATCH_SIZE, ids.length)} of ${ids.length}...`
			});

			await Promise.all(batch.map(async (company_id) => {
				try {
					const response = await api.get(`companies/${company_id}/projects`);

					(response.data || []).forEach(project => {
						projects[project.id] = {
							title: project.title || "",
							company_id
						};
					});
				} catch (error) {
					// Una compania que falle solo deja sus proyectos sin titulo; la fila
					// sigue identificada por el id del proyecto y el del grupo.
					console.log(error);
				}
			}));
		}

		return projects;
	}

	// Los envios que alimentan la pagina: los grupos que la API sigue devolviendo,
	// mas los que este navegador archivo y ya no aparecen.
	//
	// Aqui la ausencia basta como prueba de borrado, cosa que en Follow-Up no
	// pasaba: alli se miraba un proyecto, aqui se tienen todos los grupos de la
	// API delante. Si la peticion falla no se llega hasta este punto, asi que no
	// hay forma de que un error pinte el historial entero como borrado.
	buildSends = (lists) => {
		const live = (lists || []).map(list => ({
			id: list.id,
			project_id: list.project_id,
			sent_at: list.sent_at,
			contacts: list.contacts || [],
			files: Array.isArray(list.files) ? list.files : null,
			deleted: false,
			deleted_at: null
		}));

		const live_ids = new Set(live.map(send => send.id));
		const archived = [];

		readAllArchives().forEach(archive => {
			Object.keys(archive.entries).forEach(key => {
				const entry = archive.entries[key];

				if (!entry || live_ids.has(entry.id)) {
					return;
				}

				archived.push({
					id: entry.id,
					project_id: entry.project_id || Number(archive.project_id),
					sent_at: entry.sent_at,
					contacts: entry.contacts || [],
					// El archivo ya guarda los archivos del envio: la API no los tiene.
					files: entry.files || [],
					deleted: true,
					deleted_at: entry.deleted_at || null
				});
			});
		});

		return live.concat(archived);
	}

	// ----------------------------------------------------
	// AGRUPACION -----------------------------------------
	// ----------------------------------------------------

	// Indice "contact_type-contact_id" -> a que publisher pertenece el contacto.
	// Se construye de una pasada: buscar contacto por contacto entre 250+
	// publishers y sus empleados seria cuadratico y esto se recalcula en render.
	buildContactIndex = (publishers) => {
		const index = new Map();

		(publishers || []).forEach(publisher => {
			index.set(`publisher-${publisher.id}`, {
				publisher_id: publisher.id,
				name: publisher.contact_name || publisher.name || "",
				email: publisher.email || "",
				channel: "Company account"
			});

			(publisher.collaborators || []).forEach(employee => {
				index.set(`publisher_employee-${employee.id}`, {
					publisher_id: publisher.id,
					name: employee.name || "",
					email: employee.email || "",
					channel: "Contact"
				});
			});
		});

		return index;
	}

	// Un card por publisher, y dentro un envio por grupo. Un mismo grupo puede
	// alcanzar a varios publishers; entonces aparece en el card de cada uno, con
	// los destinatarios que le tocan a ese publisher.
	buildGroups = (publishers, sends) => {
		const index = this.buildContactIndex(publishers);
		const names = new Map((publishers || []).map(publisher => [
			publisher.id,
			publisher.name || publisher.contact_name || ""
		]));

		const groups = new Map();

		(sends || []).forEach(send => {
			const byPublisher = new Map();

			(send.contacts || []).forEach(contact => {
				const owner = index.get(`${contact.contact_type}-${contact.contact_id}`);

				// Los contactos de prensa no cuelgan de ningun publisher: esta pagina
				// agrupa por publisher y ahi no tienen sitio.
				if (!owner) {
					return;
				}

				if (!byPublisher.has(owner.publisher_id)) {
					byPublisher.set(owner.publisher_id, []);
				}

				byPublisher.get(owner.publisher_id).push(owner);
			});

			byPublisher.forEach((recipients, publisher_id) => {
				if (!groups.has(publisher_id)) {
					groups.set(publisher_id, {
						publisher_id,
						name: names.get(publisher_id) || "Unknown publisher",
						sends: []
					});
				}

				groups.get(publisher_id).sends.push({...send, recipients});
			});
		});

		return Array.from(groups.values())
			.map(group => {
				const sends = group.sends.slice().sort(
					(a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
				);

				const projects = new Set(sends.map(send => send.project_id));
				const contacts = new Set();

				sends.forEach(send => send.recipients.forEach(
					recipient => contacts.add(`${recipient.name}-${recipient.email}`)
				));

				return {
					...group,
					sends,
					projects_total: projects.size,
					contacts_total: contacts.size,
					sends_total: sends.length,
					sends_deleted: sends.filter(send => send.deleted).length,
					last_sent_at: sends.length ? sends[0].sent_at : null
				};
			})
			.sort((a, b) => new Date(b.last_sent_at).getTime() - new Date(a.last_sent_at).getTime());
	}

	filterGroups = (groups) => {
		const term = (this.state.search || "").trim().toLowerCase();

		if (!term) {
			return groups;
		}

		return groups.filter(group => group.name.toLowerCase().includes(term));
	}

	// ----------------------------------------------------
	// DESPLIEGUE -----------------------------------------
	// ----------------------------------------------------

	togglePublisher = async (group) => {
		const opening = this.state.open_publisher !== group.publisher_id;

		this.setState({open_publisher: opening ? group.publisher_id : null});

		if (opening) {
			await this.loadGroupDetails(group);
		}
	}

	// Los archivos de cada envio y los titulos de los archivos del proyecto se
	// piden solo al desplegar, y una vez: pedirlos de entrada para los cientos de
	// envios de la pagina seria pagarlos casi todos para nada.
	loadGroupDetails = async (group) => {
		this.setState({details_loading: true});

		// Un mismo grupo puede haber alcanzado a varios publishers, y cada card tiene
		// su propia copia de la fila. Los archivos se guardan por id de envio para no
		// volver a pedirlos y para que la copia del segundo card tambien los reciba:
		// con un simple "ya cargado" esa copia se quedaba vacia para siempre.
		group.sends.forEach(send => {
			if (send.files === null && this.loaded_list_files[send.id]) {
				send.files = this.loaded_list_files[send.id];
			}
		});

		const pending = group.sends.filter(send => send.files === null);

		await Promise.all(pending.map(async (send) => {
			try {
				const response = await api.get(`/project_submission_lists/${send.id}/files`);
				send.files = response.data || [];
			} catch (error) {
				console.log(error);
				send.files = [];
			}

			this.loaded_list_files[send.id] = send.files;
		}));

		const project_ids = Array.from(new Set(group.sends.map(send => send.project_id)))
			.filter(project_id => project_id != null && !this.loaded_project_files[project_id]);

		const file_titles = {...this.state.file_titles};

		await Promise.all(project_ids.map(async (project_id) => {
			const project = this.state.projects[project_id];

			// La ruta de archivos cuelga de la compania; sin el proyecto resuelto no
			// se sabe de cual, y la celda se queda con el conteo a secas.
			if (!project || project.company_id == null) {
				return;
			}

			try {
				const response = await api.get(`companies/${project.company_id}/projects/${project_id}/files`);

				(response.data || []).forEach(file => {
					file_titles[file.id] = file.title;
				});

				this.loaded_project_files[project_id] = true;
			} catch (error) {
				console.log(error);
			}
		}));

		this.setState({details_loading: false, file_titles});
	}

	// ----------------------------------------------------
	// FORMATO --------------------------------------------
	// ----------------------------------------------------

	// La zona del navegador acompana a cada fecha: la API guarda instantes UTC y
	// cada navegador los traduce a la suya, asi que un "8:00 PM" a secas se lee
	// mal cuando quien envio y quien revisa estan en paises distintos.
	browserTimeZone = () => {
		try {
			return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
		} catch (error) {
			return "";
		}
	}

	formatDate = (value) => {
		const date = new Date(value);

		if (isNaN(date.getTime())) {
			return "";
		}

		const zone = this.browserTimeZone();

		return `${date.toLocaleString(undefined, {timeZoneName: "short"})}${zone ? ` (${zone})` : ""}`;
	}

	formatDateShort = (value) => {
		const date = new Date(value);

		if (isNaN(date.getTime())) {
			return "";
		}

		return date.toLocaleString(undefined, {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit"
		});
	}

	projectTitle = (project_id) => {
		const project = this.state.projects[project_id];

		return (project && project.title) || `Project #${project_id}`;
	}

	companyName = (project_id) => {
		const project = this.state.projects[project_id];

		if (!project || project.company_id == null) {
			return "";
		}

		return this.state.companies.get(project.company_id) || `Company #${project.company_id}`;
	}

	// ----------------------------------------------------
	// RENDER ---------------------------------------------
	// ----------------------------------------------------

	renderDate = (value) => {
		if (!value) {
			return <span className="history-empty">--</span>;
		}

		return (
			<span className="history-chip" title={this.formatDate(value)}>
				{this.formatDateShort(value)}
			</span>
		);
	}

	renderRecipients = (recipients) => {
		if (!recipients.length) {
			return <span className="history-empty">--</span>;
		}

		const detail = recipients
			.map(recipient => `${recipient.channel}: ${recipient.name}${recipient.email ? ` (${recipient.email})` : ""}`)
			.join(" | ");

		return (
			<span className="history-recipients" title={detail}>
				{recipients.map(recipient => recipient.name).join(", ")}
			</span>
		);
	}

	renderFiles = (send) => {
		// null = todavia no se han pedido; [] = se pidieron y no habia ninguno.
		if (send.files === null) {
			return <span className="history-empty">--</span>;
		}

		if (!send.files.length) {
			return <span className="history-empty" title="No files were unlocked in this send">None</span>;
		}

		const titles = send.files.map(file => this.state.file_titles[file.file_id] || `File #${file.file_id}`);

		return (
			<span className="badge history-files" title={titles.join(", ")}>
				{`${send.files.length} file${send.files.length === 1 ? "" : "s"}`}
			</span>
		);
	}

	renderStatus = (send) => {
		if (!send.deleted) {
			return <span className="history-status is-active">Active</span>;
		}

		const detail = send.deleted_at
			? `Group deleted ${this.formatDate(send.deleted_at)}. The send still happened.`
			: "The group no longer exists in the API. The send still happened.";

		return <span className="history-status is-deleted" title={detail}>Deleted</span>;
	}

	renderGroup = (group) => {
		const open = this.state.open_publisher === group.publisher_id;

		return (
			<div className={`publisher-card ${open ? "opened" : ""}`} key={group.publisher_id}>
				<button
					className={`publisher-card-head ${open ? "opened" : ""}`}
					onClick={() => this.togglePublisher(group)}
					aria-expanded={open}
				>
					<span className="publisher-card-name">{group.name}</span>

					<span className="publisher-card-meta">
						<span className="badge-collabs">{`${group.projects_total} project${group.projects_total === 1 ? "" : "s"}`}</span>
						<span className="badge-collabs">{`${group.sends_total} send${group.sends_total === 1 ? "" : "s"}`}</span>
						<span className="badge-collabs">{`${group.contacts_total} contact${group.contacts_total === 1 ? "" : "s"}`}</span>

						{ group.sends_deleted > 0 &&
							<span
								className="history-status is-deleted"
								title={`${group.sends_deleted} of these sends come from a group that was deleted`}
							>
								{`${group.sends_deleted} deleted`}
							</span>
						}

						{ group.last_sent_at &&
							<span className="history-chip" title={this.formatDate(group.last_sent_at)}>
								{`Last ${this.formatDateShort(group.last_sent_at)}`}
							</span>
						}

						<span className="history-caret" aria-hidden="true"></span>
					</span>
				</button>

				{ open &&
					<React.Fragment>
						<div className="project-content-header history-header">
							<div className="col flex align-center sm-12 md-3 lg-3">
								<p className="title">Project</p>
							</div>
							<div className="col flex align-center sm-12 md-2 lg-2">
								<p className="title">Company</p>
							</div>
							<div className="col flex align-center sm-12 md-2 lg-2">
								<p className="title">Sent</p>
							</div>
							<div className="col flex align-center sm-12 md-3 lg-3">
								<p className="title">Contacts</p>
							</div>
							<div className="col flex align-center sm-12 md-1 lg-1">
								<p className="title">Files</p>
							</div>
							<div className="col flex align-center sm-12 md-1 lg-1">
								<p className="title">Status</p>
							</div>
						</div>

						<div className="project-content-body">
							{ this.state.details_loading &&
								<div className="new-selection history-notice">
									<div className="col flex align-center sm-12">
										<p className="text">Loading files for these sends...</p>
									</div>
								</div>
							}

							{ group.sends.map(send => (
								<div
									className={`publisher-selection history-row ${send.deleted ? "is-deleted" : ""}`}
									key={`${group.publisher_id}-${send.id}`}
								>
									<div className="col flex align-center sm-12 md-3 lg-3 history-project">
										<p className="text">{this.projectTitle(send.project_id)}</p>
										<span className="history-ids">{`Group #${send.id} - Project #${send.project_id}`}</span>
									</div>

									<div className="col flex align-center sm-12 md-2 lg-2">
										<span className="history-label">Company</span>
										<span className="history-text">{this.companyName(send.project_id) || "--"}</span>
									</div>

									<div className="col flex align-center sm-12 md-2 lg-2">
										<span className="history-label">Sent</span>
										{this.renderDate(send.sent_at)}
									</div>

									<div className="col flex align-center sm-12 md-3 lg-3">
										<span className="history-label">Contacts</span>
										{this.renderRecipients(send.recipients)}
									</div>

									<div className="col flex align-center sm-12 md-1 lg-1">
										<span className="history-label">Files</span>
										{this.renderFiles(send)}
									</div>

									<div className="col flex align-center sm-12 md-1 lg-1">
										<span className="history-label">Status</span>
										{this.renderStatus(send)}
									</div>
								</div>
							))}
						</div>
					</React.Fragment>
				}
			</div>
		);
	}

	render() {
		if (this.state.not_admin) {
			return <Redirect to='/projects' />;
		}

		const groups = this.filterGroups(this.state.groups);
		const total_sends = groups.reduce((total, group) => total + group.sends_total, 0);

		return (
			<div data-page="project">
				<Header />

				<TitlePage title="Submission History"/>

				<div className="container-fluid" data-component="project-content">
					<div className="container-fluid">
						<div className="project-header row">
							<h1 className="title">Submission History</h1>
						</div>

						{/* history-content trae la tabla de historial ya resuelta en
						    project.scss: chips, etiquetas en movil y filas. submission-history
						    solo anade lo propio de esta pagina. */}
						<div className="project-content row history-content submission-history">
							<div className="title-content with-indicator">
								<span>Publishers</span>

								{ !this.state.loading &&
									<span className="queue-indicator">
										{`${groups.length} publisher${groups.length === 1 ? "" : "s"} - ${total_sends} send${total_sends === 1 ? "" : "s"}`}
									</span>
								}
							</div>

							<div className="history-filters">
								<div className="search">
									<Search
										onSubmit={(e) => e.preventDefault()}
										placeholder="Search publisher"
										value={this.state.search}
										onChange={(e) => this.setState({search: e.target.value})}
									/>
								</div>

								<p className="history-note">
									Read-only view. Nothing is sent from this page.
								</p>
							</div>

							{ this.state.loading &&
								<React.Fragment>
									<span className="laoding-lists"></span>
									{ this.state.loading_step !== "" &&
										<div className="new-selection history-notice">
											<div className="col flex align-center sm-12">
												<p className="text">{this.state.loading_step}</p>
											</div>
										</div>
									}
								</React.Fragment>
							}

							{ !this.state.loading && this.state.error !== "" &&
								<div className="new-selection history-notice">
									<div className="col flex align-center sm-12 md-8 lg-9">
										<p className="text">{this.state.error}</p>
									</div>
									<div className="col flex align-center sm-12 md-4 lg-3">
										<button className="btn blue" onClick={() => this.loadHistory()}>Retry</button>
									</div>
								</div>
							}

							{ !this.state.loading && this.state.error === "" && groups.length === 0 &&
								<div className="new-selection history-notice">
									<div className="col flex align-center sm-12">
										<p className="text">
											{ this.state.search
												? "No publisher matches that search."
												: "No projects have been sent to any publisher yet." }
										</p>
									</div>
								</div>
							}

							{ !this.state.loading && this.state.error === "" &&
								groups.map(group => this.renderGroup(group))
							}
						</div>
					</div>
				</div>
			</div>
		);
	}
}
